// ============================================
// FAVORITES PAGE
// ============================================

import { createFileRoute, Link } from '@tanstack/react-router';
import { Heart, Ship, Trash2, Loader2 } from 'lucide-react';
import { useFavoriteJobs, useRemoveFavoriteJob } from '@/hooks';
import { formatCurrency, formatRelativeTime } from '@/utils';
import { useTranslation } from '@crew-at-sea/i18n';

export const Route = createFileRoute('/_authenticated/favorites')({
  component: FavoritesPage,
});

function FavoritesPage() {
  const { t } = useTranslation(['panel', 'common', 'enums']);
  const { data: favoritesResponse, isLoading } = useFavoriteJobs();
  const removeFavorite = useRemoveFavoriteJob();

  const favorites: any[] = (favoritesResponse as any)?.data || favoritesResponse || [];

  const handleRemove = (jobId: string) => {
    removeFavorite.mutate(jobId);
  };

  if (isLoading) {
    return <div className="flex items-center justify-center h-64"><Loader2 className="w-8 h-8 animate-spin text-maritime-600" /></div>;
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">{t('panel:favorites.title')}</h1>
        <p className="text-gray-500 mt-1">{t('panel:favorites.count', { count: favorites.length })}</p>
      </div>

      {favorites.length === 0 ? (
        <div className="card p-12 text-center">
          <Heart className="w-16 h-16 mx-auto text-gray-300 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">{t('panel:favorites.noFavorites')}</h3>
          <p className="text-gray-500 mb-4">{t('panel:favorites.addFavoritesHint')}</p>
          <Link to="/jobs" className="btn-primary">{t('panel:favorites.viewJobs')}</Link>
        </div>
      ) : (
        <div className="space-y-4">
          {favorites.map((fav: any) => {
            const job = fav.job;
            if (!job) return null;
            return (
              <div key={fav.id} className="card p-4 hover:shadow-md transition-shadow">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-maritime bg-maritime-100 flex items-center justify-center flex-shrink-0">
                    <Ship className="w-6 h-6 text-maritime-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <Link to="/jobs/$slug" params={{ slug: job.slug }} className="font-medium text-gray-900 hover:text-maritime-600">
                          {job.title}
                        </Link>
                        <p className="text-sm text-gray-500">{job.company?.name}</p>
                      </div>
                      <button
                        onClick={() => handleRemove(job.id)}
                        className="p-2 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-maritime"
                        disabled={removeFavorite.isPending}
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                    </div>
                    <div className="flex flex-wrap items-center gap-2 mt-2">
                      <span className="badge badge-info">{t(`enums:vesselType.${job.vesselType}`, { defaultValue: job.vesselType })}</span>
                      <span className="badge badge-gray">{t(`enums:department.${job.department}`, { defaultValue: job.department })}</span>
                    </div>
                    <div className="flex items-center gap-4 mt-2 text-sm text-gray-500">
                      {job.minSalary && <span>{formatCurrency(job.minSalary, job.currency)}</span>}
                      <span>•</span>
                      <span>{t('panel:favorites.added')}: {formatRelativeTime(fav.createdAt)}</span>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
